import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import WhatsAppIcon from "./WhatsAppIcon";
import { WHATSAPP_URL } from "../utility/support";
import { isCheckoutPath, useCheckoutT } from "../i18n/checkout";
import "./WhatsAppHelp.css";

// A WhatsApp button for guests in the order checkout and the quote request
// (Alejo, 2026-10-22): someone with a question talks to a person instead of
// leaving. Alejo answers it, 8 AM to 11 PM Eastern. Signed-in clients have the
// order's Messages instead, so they don't see it.
//
// Same look and behavior as the static articles' button (public/seo/wa.js):
// once per visit (shared sessionStorage key), after a while on the checkout,
// a note next to it that can be closed. On phones it sits above the continue
// bar (MobileContinueBar), whose height changes with the step.

const NOTE_KEY = "everbary.whatsappNote";
const NOTE_DELAY_MS = 30000;

const awakeNow = () => {
  try {
    const h = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", hourCycle: "h23" }).format(new Date()));
    return h >= 8 && h < 23;
  } catch (e) {
    return true;
  }
};

const noteSeen = () => {
  try {
    return !!window.sessionStorage.getItem(NOTE_KEY);
  } catch (e) {
    return true;
  }
};

const markNoteSeen = () => {
  try {
    window.sessionStorage.setItem(NOTE_KEY, "1");
  } catch (e) {
    // No storage: the note can show again on another step.
  }
};

const track = (placement) => {
  try {
    if (typeof window.gtag === "function") window.gtag("event", "whatsapp_click", { page_path: window.location.pathname, placement });
  } catch (e) {
    // optional
  }
  try {
    if (typeof window.clarity === "function") window.clarity("event", "whatsapp_click");
  } catch (e) {
    // optional
  }
};

const TEXTS = {
  en: {
    label: "Message us on WhatsApp",
    close: "Close",
    note: (awake) => (awake
      ? "Questions about your translation? Message us on WhatsApp. A person will reply."
      : "Questions about your translation? Message us on WhatsApp and we'll reply first thing in the morning."),
    message: (quote) => (quote ? "Hi, I have a question about my quote request." : "Hi, I have a question about my translation order."),
  },
  es: {
    label: "Escríbanos por WhatsApp",
    close: "Cerrar",
    note: (awake) => (awake
      ? "¿Tiene dudas sobre su traducción? Escríbanos por WhatsApp: le responde una persona."
      : "¿Tiene dudas sobre su traducción? Escríbanos por WhatsApp y le respondemos a primera hora de la mañana."),
    message: (quote) => (quote ? "Hola, tengo una consulta sobre mi solicitud de cotización." : "Hola, tengo una consulta sobre mi pedido de traducción."),
  },
  pt: {
    label: "Fale conosco pelo WhatsApp",
    close: "Fechar",
    note: (awake) => (awake
      ? "Tem dúvidas sobre sua tradução? Fale conosco pelo WhatsApp: uma pessoa responde."
      : "Tem dúvidas sobre sua tradução? Fale conosco pelo WhatsApp e respondemos logo cedo pela manhã."),
    message: (quote) => (quote ? "Olá, tenho uma dúvida sobre minha solicitação de orçamento." : "Olá, tenho uma dúvida sobre meu pedido de tradução."),
  },
};

// Bottom offset: above the continue bar when the step shows one.
const useBottom = (active) => {
  const [bottom, setBottom] = useState(16);
  useEffect(() => {
    if (!active) return undefined;
    const measure = () => {
      const bar = document.querySelector(".mobileContinueBar");
      // offsetParent is always null for a fixed element: getClientRects says
      // whether it is drawn at all.
      const height = bar && bar.getClientRects().length ? bar.getBoundingClientRect().height : 0;
      setBottom(height ? Math.round(height) + 12 : 16);
    };
    measure();
    const id = window.setInterval(measure, 500);
    return () => window.clearInterval(id);
  }, [active]);
  return bottom;
};

const WhatsAppHelp = ({ signedIn, loadingAuth }) => {
  const { pathname } = useLocation();
  const { lang } = useCheckoutT();
  const active = !signedIn && !loadingAuth && isCheckoutPath(pathname);
  const bottom = useBottom(active);
  const [note, setNote] = useState(false);

  useEffect(() => {
    if (!active || noteSeen()) return undefined;
    const id = window.setTimeout(() => {
      if (noteSeen()) return;
      markNoteSeen();
      setNote(true);
    }, NOTE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [active]);

  if (!active) return null;
  const t = TEXTS[lang] || TEXTS.en;
  const url = `${WHATSAPP_URL}?text=${encodeURIComponent(t.message(pathname.startsWith("/quote")))}`;
  const open = () => {
    markNoteSeen();
    setNote(false);
    track("checkout");
  };

  return (
    <div className="waHelp" style={{ bottom }}>
      {note && (
        <div
          className="waHelpNote"
          role="status"
          onClick={() => {
            open();
            // The ad reference, as on a clicked link (tools/visit-origin.js).
            window.open(typeof window.evWaHref === "function" ? window.evWaHref(url) : url, "_blank", "noopener");
          }}
        >
          {t.note(awakeNow())}
          <button
            type="button"
            className="waHelpClose"
            aria-label={t.close}
            onClick={(e) => {
              e.stopPropagation();
              setNote(false);
            }}
          >
            ×
          </button>
        </div>
      )}
      <a className="waHelpButton" href={url} target="_blank" rel="noopener noreferrer" aria-label={t.label} title={t.label} onClick={open}>
        <WhatsAppIcon size={28} />
      </a>
    </div>
  );
};

export default WhatsAppHelp;
